import { Component, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { MyMonitoringService } from './appinsights.service';
import { Globals, LANGUAGES } from './pages/services/globals';
import { ModalPortalService } from '@core/services/common/modal-portal.service';
import { RootService } from '@shared/services/rootService.service';
import { environment } from 'src/environments/environment';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { ArkikStatusService } from '@core/services/common/arkik-status.service';
import { ArkikDialogService } from '@shared/components/arkik-dialog/arkik-dialog.service';
import { TranslationService } from '@core/services/common/translation.service';
import { LoginService } from './store/login/login.service';
import { UpdateCommonDataService } from '@core/services/common/updatecommondata.service';
import { Store } from '@ngrx/store';
import { selectUserRolesLoading } from './store/user-roles/user-roles.selectors';
import { selectPlantListCompanyLoading } from './store/plant-list-company/plant-list-company.selectors';
import { selectMenuByUserLoading } from './store/menu-by-user/menu-by-user.selectors';
import { loginLoading } from './store/login/login.selectors';
import { NavigationRouteService } from '@shared/services/navigation-route.service';
import { Meta, Title } from '@angular/platform-browser';
import { CatalogLogoutService } from '@core/services/common/catalog-logout.service';
import { EventsService } from '@shared/services/common/services/events.service';
import { ArkikAlertService } from '@shared/components/arkik-alert/arkik-alert.service';
import { PwaInstallService } from '@shared/services/pwa/pwa-install.service';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy, OnInit {
  @ViewChild('dynamicComponentContainer', { read: ViewContainerRef, static: true })
  container: ViewContainerRef;
  private readonly _destroying$ = new Subject<void>();
  private _refreshTokenTimer: ReturnType<typeof setTimeout> | null = null;
  private _accessTokenExpirySeconds = 0;
  private readonly _universalLinkNotRunningAlertId = 'universal-link-not-running';
  showLogoLoader = false;
  showCoverage = false;
  mainDataLoaded = false;
  showCommonHeader = false;

  constructor(
    private globals: Globals,
    private router: Router,
    private myMonitoringService: MyMonitoringService,
    public portal: ModalPortalService,
    private rs: RootService,
    private msalBroadcastService: MsalBroadcastService,
    private authServiceB2C: MsalService,
    private arkikStatus: ArkikStatusService,
    private _dialog: ArkikDialogService,
    private t: TranslationService,
    private loginService: LoginService,
    private updateCommonDataService: UpdateCommonDataService,
    private store: Store<Store>,
    private navigationRouteService: NavigationRouteService,
    private meta: Meta,
    private title: Title,
    private catalogLogout: CatalogLogoutService,
    private eventsService: EventsService,
    private alertService: ArkikAlertService,
    private pwaInstallService: PwaInstallService,
  ) {
    this.pwaInstallService.initialize();
  }

  loadScript(url: string): void {
    const script = document.createElement('script');
    script.src = url;
    script.type = 'text/javascript';
    script.async = true;
    script.crossOrigin = 'anonymous';
    document.head.appendChild(script);
  }

  async ngOnInit() {
    this._setMetaTags();
    if (environment?.DynatraceSnippet) {
      this.loadScript(environment?.DynatraceSnippet);
    }
    this._handleLogin();
    this._handleCommonHeader();
    //Get the language from the session storage
    const ssValue = sessionStorage.getItem('lang');
    // Assign the language to the global variable
    this.globals.language = ssValue ? ssValue.toLocaleLowerCase().replace('_', '-') : 'es-mx';
    this.rs.container = this.container;
    this._msalBroadcastService();

    this.arkikStatus.universalLinkRunningStatus$.pipe(takeUntil(this._destroying$)).subscribe((isRunning) => {
      this.alertService.removeAlertById(this._universalLinkNotRunningAlertId);

      if (!isRunning) {
        this.alertService.warning(
          this.t.pt('views.globals.universal-link-not-running'),
          null,
          true,
          this._universalLinkNotRunningAlertId,
        );
      }
    });
  }

  /**
   * Handles the login process when the URL hash contains a 'state' parameter.
   * If present, it shows the coverage and logo loader, and triggers the initial login flow.
   */
  private _handleLogin(): void {
    const hasAuthParams = sessionStorage.getItem('hasAuthParams');

    if (hasAuthParams && !sessionStorage.getItem('firstLoginHandled')) {
      sessionStorage.setItem('firstLoginHandled', '1');
      sessionStorage.setItem('initLoader', '1');
      this.loginService.initialLogin$.next(true);
    }

    this.eventsService.hashDetected$.pipe(takeUntil(this._destroying$)).subscribe((hashDetected) => {
      const hasAuthParams = sessionStorage.getItem('hasAuthParams');

      if ((hashDetected || hasAuthParams) && !sessionStorage.getItem('firstLoginHandled')) {
        sessionStorage.setItem('firstLoginHandled', '1');
        sessionStorage.setItem('initLoader', '1');
        this.loginService.initialLogin$.next(true);
      }
    });
  }

  private _handleCommonHeader() {
    this.navigationRouteService.enableCommonHeader(this.router.url);
    this.navigationRouteService.showCommonHeader$.pipe(takeUntil(this._destroying$)).subscribe((showHeader) => {
      this.showCommonHeader = showHeader;
    });
  }

  hasStateInHash(): boolean {
    const hash = window.location.hash; // Ejemplo: "#state=eyJpZCI6IjNkNTA2O"
    return /state=([^&]+)/.test(hash);
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    if (this._refreshTokenTimer) {
      clearTimeout(this._refreshTokenTimer);
    }
  }

  private _msalBroadcastService() {
    this.msalBroadcastService.msalSubject$.pipe(takeUntil(this._destroying$)).subscribe((result: EventMessage) => {
      switch (result.eventType) {
        case EventType.LOGOUT_SUCCESS:
          this._clearData();
          break;
        case EventType.LOGIN_SUCCESS: {
          this._handleInitialLogin();
          const payload = result.payload as AuthenticationResult;
          this.authServiceB2C.instance.setActiveAccount(payload.account);
          this.router.navigate(['/login'], { replaceUrl: true });
          this._scheduleRefreshTokenCheck(payload);
          break;
        }

        case EventType.ACQUIRE_TOKEN_SUCCESS:
          this._scheduleRefreshTokenCheck(result.payload as AuthenticationResult);
          break;

        case EventType.ACQUIRE_TOKEN_FAILURE:
          this._handleAcquireTokenFailure(result);
          break;

        case EventType.LOGIN_FAILURE:
          // AuthRedirectComponent already shows the error modal and redirects to sign-in for this event
          break;

        case EventType.LOGOUT_FAILURE:
          if (result.error && result.error.message.indexOf('endpoints_resolution_error') > -1) {
            this.router.navigate(['/logout-offline']);
          }
          break;
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();

        const hasAuthParams = sessionStorage.getItem('hasAuthParams');

        const account = this.authServiceB2C.instance.getActiveAccount();

        if (hasAuthParams && account && !sessionStorage.getItem('logoPlayed')) {
          sessionStorage.setItem('logoPlayed', '1');

          this._showInitialLogo();

          this.loginService.initialLogin$.next(true);

          this._handleInitialLogin();
        }
      });
  }

  private async _clearData(): Promise<void> {
    try {
      await this.catalogLogout.start();
    } catch (error) {
      console.error('[AppComponent] catalog logout cleanup failed', error);
      return;
    }
    this._clearMsalStorage();
    sessionStorage.clear();
  }

  private _clearMsalStorage(): void {
    Object.keys(localStorage).forEach((key) => {
      if (this._matchesAuthStorageKey(key)) {
        localStorage.removeItem(key);
      }
    });

    Object.keys(sessionStorage).forEach((key) => {
      if (this._matchesAuthStorageKey(key)) {
        sessionStorage.removeItem(key);
      }
    });

    const cookieDomains = this._getMsalCookieDomains();
    const cookiePaths = this._getAuthCookiePaths();
    document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .filter((cookie) => this._matchesAuthCookieName(cookie.split('=')[0] ?? ''))
      .forEach((cookie) => {
        const cookieName = cookie.split('=')[0];
        cookiePaths.forEach((path) => {
          document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
          cookieDomains.forEach((domain) => {
            document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain}`;
          });
        });
      });
  }

  private _matchesAuthStorageKey(key: string): boolean {
    const normalizedKey = key.toLowerCase();
    return normalizedKey.startsWith('msal.') || normalizedKey === 'msal';
  }

  private _matchesAuthCookieName(cookieName: string): boolean {
    const normalizedName = cookieName.toLowerCase().trim();
    return normalizedName.startsWith('msal.') || normalizedName === 'msal' || normalizedName.startsWith('x-ms-cpim-');
  }

  private _getMsalCookieDomains(): string[] {
    const domains = new Set<string>();

    const envHost = this._extractHostname(environment.domain);
    if (envHost && envHost !== 'localhost') {
      domains.add(envHost);
      domains.add(`.${envHost}`);
    }

    const currentHost = window.location.hostname;
    if (currentHost && currentHost !== 'localhost') {
      domains.add(currentHost);
      domains.add(`.${currentHost}`);
    }

    return Array.from(domains);
  }

  private _getAuthCookiePaths(): string[] {
    const paths = new Set<string>(['/']);
    const currentPath = window.location.pathname;

    if (!currentPath || currentPath === '/') {
      return Array.from(paths);
    }

    const parts = currentPath.split('/').filter(Boolean);
    let builtPath = '';
    parts.forEach((part) => {
      builtPath += `/${part}`;
      paths.add(builtPath);
    });

    return Array.from(paths);
  }

  private _extractHostname(urlOrHost: string): string {
    if (!urlOrHost) {
      return '';
    }

    try {
      return new URL(urlOrHost).hostname.toLowerCase();
    } catch {
      const normalized = urlOrHost
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .split('/')[0]
        .split(':')[0];

      return normalized;
    }
  }

  private _handleAcquireTokenFailure(result: EventMessage): void {
    const transientCodes = [
      'no_network_connectivity',
      'network_error',
      'timed_out',
      'monitor_window_timeout',
      'failed_to_parse_response',
    ];

    const errorCode = this._extractMsalErrorCode(result.error);
    const isInteractionRequired = result.error instanceof InteractionRequiredAuthError;
    const isTransient = !navigator.onLine || (!!errorCode && transientCodes.includes(errorCode));

    if (isTransient && !isInteractionRequired) {
      return;
    }

    this._logoutB2CSession();
  }

  private _extractMsalErrorCode(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return '';
    }

    const maybeError = error as { errorCode?: string; code?: string };
    return (maybeError.errorCode ?? maybeError.code ?? '').toLowerCase();
  }

  private async _logoutB2CSession(): Promise<void> {
    try {
      await this.catalogLogout.start();
    } catch (error) {
      console.error('[AppComponent] unsafe logout redirect blocked', error);
      return;
    }
    let logoutRoute = '/cetify/home/index';
    if (environment.isLocalWeb) {
      logoutRoute = this.arkikStatus.isArkikOnline() ? environment.B2C_signout : '/logout-offline';
      this.authServiceB2C.logoutRedirect({ postLogoutRedirectUri: logoutRoute });
      return;
    }

    this.router.navigate([logoutRoute], { queryParams: { logoutcertify: true } });
    this.authServiceB2C.logoutRedirect({ postLogoutRedirectUri: environment.B2C_signout });
  }

  /**
   * Reads MSAL localStorage cache to find refresh token expiry (Unix epoch seconds).
   */
  private _getRefreshTokenExpirySeconds(account: AccountInfo | null): number {
    if (!account) {
      return 0;
    }

    try {
      const homeAccountId = account.homeAccountId.toLowerCase();
      const accountEnvironment = account.environment.toLowerCase();

      for (const key of Object.keys(localStorage)) {
        const normalizedKey = key.toLowerCase();
        const isCurrentAccountToken =
          normalizedKey.includes('refreshtoken') &&
          normalizedKey.includes(homeAccountId) &&
          normalizedKey.includes(accountEnvironment);

        if (isCurrentAccountToken) {
          const entry = JSON.parse(localStorage.getItem(key) ?? '{}') as { expiresOn?: string };
          if (entry.expiresOn) {
            const expiry = parseInt(entry.expiresOn, 10);
            if (!Number.isNaN(expiry)) {
              return expiry;
            }
          }
        }
      }
    } catch {
      // Ignore cache read errors.
    }
    return 0;
  }

  /**
   * Proactively refresh token before refresh token expiry window causes a 400.
   */
  private _scheduleRefreshTokenCheck(result?: AuthenticationResult): void {
    if (this._refreshTokenTimer) {
      clearTimeout(this._refreshTokenTimer);
      this._refreshTokenTimer = null;
    }

    const activeAccount = this.authServiceB2C.instance.getActiveAccount();
    const refreshTokenExpirySeconds = this._getRefreshTokenExpirySeconds(activeAccount);

    if (result?.expiresOn) {
      this._accessTokenExpirySeconds = Math.floor(result.expiresOn.getTime() / 1000);
    }

    if (!refreshTokenExpirySeconds) {
      return;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    const secondsUntilExpiry = refreshTokenExpirySeconds - nowSeconds;

    if (secondsUntilExpiry <= 0) {
      this._forceTokenRenewal();
      return;
    }

    const maxWindowSeconds =
      this._accessTokenExpirySeconds > nowSeconds ? this._accessTokenExpirySeconds - nowSeconds : secondsUntilExpiry;

    if (secondsUntilExpiry > maxWindowSeconds) {
      return;
    }

    const delayMs = (secondsUntilExpiry - 2 * 60) * 1000;

    if (delayMs <= 0) {
      this._forceTokenRenewal();
      return;
    }

    this._refreshTokenTimer = setTimeout(() => this._forceTokenRenewal(), delayMs);
  }

  private _forceTokenRenewal(): void {
    const account = this.authServiceB2C.instance.getActiveAccount();
    if (!account) {
      return;
    }

    this.authServiceB2C
      .acquireTokenSilent({
        account,
        scopes: [`https://${environment.B2C_tenantName}.onmicrosoft.com/b2c-api/b2c.read`],
        forceRefresh: true,
      })
      .subscribe({
        next: () => {
          this._scheduleRefreshTokenCheck();
        },
        error: () => {
          // ACQUIRE_TOKEN_FAILURE event handles logout.
        },
      });
  }

  checkAndSetActiveAccount() {
    if (this.authServiceB2C.instance.getAllAccounts().length > 0) {
      const accounts = this.authServiceB2C.instance.getAllAccounts();

      accounts.forEach((currentValue) => {
        if (currentValue.environment === `${environment.B2C_tenantName}.b2clogin.com`) {
          this._setActiveAccount(currentValue);
        }
      });
    }
  }

  private _setActiveAccount(account: AccountInfo) {
    const activeAccount = this.authServiceB2C.instance.getActiveAccount();

    if (!activeAccount && this.authServiceB2C.instance.getAllAccounts().length > 0) {
      this.authServiceB2C.instance.setActiveAccount(account);
      this.router.navigate(['/login'], { replaceUrl: true });
    }
  }

  onLogoAnimationFinished(): void {
    setTimeout(() => {
      this.showLogoLoader = false;
      this.showCoverage = false;
    }, 1000);
  }

  private _showInitialLogo(): void {
    this.showCoverage = true;
    this.showLogoLoader = true;

    setTimeout(() => {
      this.showLogoLoader = false;
      this.showCoverage = false;

      sessionStorage.removeItem('firstLoginHandled');
      sessionStorage.removeItem('logoPlayed');
      sessionStorage.removeItem('hasAuthParams');
    }, 3000);
  }

  private _handleInitialLogin(): void {
    const profileData$ = this.updateCommonDataService.profileData$;
    const userRoleLoading$ = this.store.select(selectUserRolesLoading).pipe(takeUntil(this._destroying$));
    const plantListCompanyLoading$ = this.store
      .select(selectPlantListCompanyLoading)
      .pipe(takeUntil(this._destroying$));
    const menuByUserLoading$ = this.store.select(selectMenuByUserLoading).pipe(takeUntil(this._destroying$));
    const loginLoading$ = this.store.select(loginLoading).pipe(takeUntil(this._destroying$));

    combineLatest([profileData$, userRoleLoading$, plantListCompanyLoading$, menuByUserLoading$, loginLoading$])
      .pipe(
        filter(([profileData, userRoleLoading, plantListCompanyLoading, menuByUserLoading, loginLoading]) => {
          return (
            profileData &&
            Object.keys(profileData)?.length > 0 &&
            userRoleLoading !== undefined &&
            userRoleLoading === false &&
            plantListCompanyLoading !== undefined &&
            plantListCompanyLoading === false &&
            menuByUserLoading !== undefined &&
            menuByUserLoading === false &&
            loginLoading !== undefined &&
            loginLoading === false
          );
        }),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this._reviewMainDataLoaded();
      });
  }

  private _reviewMainDataLoaded(): void {
    this.mainDataLoaded = true;
  }

  private _setMetaTags() {
    const fullLang = navigator.language.toLowerCase();
    this.title.setTitle(
      this.globals.language === LANGUAGES.ES_MX || fullLang === LANGUAGES.ES_MX
        ? MetaTagsContent.TITLE_ES
        : MetaTagsContent.TITLE_EN,
    );
    this.meta.updateTag({
      name: MetaTagsNames.DESCRIPTION,
      content:
        this.globals.language === LANGUAGES.ES_MX || fullLang === LANGUAGES.ES_MX
          ? MetaTagsContent.DESCRIPTION_ES
          : MetaTagsContent.DESCRIPTION_EN,
    });
    this.meta.updateTag({
      name: MetaTagsNames.KEYWORDS,
      content: MetaTagsContent.KEYWORDS,
    });
    this.meta.updateTag({
      property: MetaTagsNames.OG_TITLE,
      content: MetaTagsContent.OG_TITLE,
    });
    this.meta.updateTag({
      property: MetaTagsNames.OG_URL,
      content: MetaTagsContent.OG_URL,
    });
    this.meta.updateTag({
      property: MetaTagsNames.OG_DESCRIPTION,
      content:
        this.globals.language === LANGUAGES.ES_MX || fullLang === LANGUAGES.ES_MX
          ? MetaTagsContent.DESCRIPTION_ES
          : MetaTagsContent.DESCRIPTION_EN,
    });
  }
}

export const MetaTagsNames = {
  TITLE: 'title',
  DESCRIPTION: 'description',
  KEYWORDS: 'keywords',
  OG_TITLE: 'og:title',
  OG_URL: 'og:url',
  OG_DESCRIPTION: 'og:description',
};

export const MetaTagsContent = {
  TITLE_ES: 'Arkik',
  TITLE_EN: 'Arkik',
  DESCRIPTION_ES:
    'Arkik es un sistema integrado para la automatización de plantas de concreto. Optimiza tus procesos con producción automatizada, monitoreo en tiempo real, sistemas de dosificación y control por computadora. Reduce costos, mejora la calidad del producto final y aumenta la rentabilidad de tu planta.',
  DESCRIPTION_EN:
    'Arkik is an integrated system for concrete plant automation. Optimize your processes with automated production, real-time monitoring, batching systems, and computer-based control. Reduce costs, improve final product quality, and boost your plant is profitability.',
  KEYWORDS:
    'Planta de concreto automatizada, Automated concrete plant, Dosificador, Dosifier, Automatización de plantas de concreto, Concrete plant automation, Planta de dosificación de concreto, Concrete batching plant, Mezcladora de concreto, Concrete mixer, Sistema de control en plantas de concreto, Control system in concrete plants, Sistema de control por computadora en plantas de concreto, Computer control system in concrete plants, Monitoreo en tiempo real en plantas de concreto, Real-time monitoring in concrete plants, Sistema de pesaje de agregados, Aggregate weighing system, Sistema de dosificación automatizado, Automated batching system, Estación de mezcla de concreto, Concrete mixing station, Operación remota en plantas de concreto, Remote operation in concrete plants',
  OG_TITLE: 'Arkik',
  OG_URL: 'https://www.arkik.com/',
  OG_DESCRIPTION: 'Integrated system designed for the efficient management of concrete plants',
};
